// @ts-nocheck
// ═══ RUNTIME (Preview / skip-logic engine) ════════════════
// Ported verbatim from legacy/EMR Forms.html. Covers tab switching (show),
// the step-by-step + section-at-a-time fill engine, the universal input
// builder (buildRtInput — every question type), value readers, per-type
// validation, the branch/skip resolver (resolveNext / attachSkipListeners /
// goNext with the byValue → defaultNext → next-in-order priority order and the
// secHistory retrace stack), block dialog, progress bar, geolocation capture,
// and response submission (POST via axios, preserving the legacy on-failure
// server-status toggle).

import type { Ctx } from './engineTypes.ts';
import { esc, isST, sidFT } from './state.ts';
import { COUNTRIES, countryByIso, validatePhone, parsePhoneVal, fmtPhoneDisplay } from './countries.ts';
import { postResponse, postPublicResponse } from './api.ts';

export function attachRuntime(ctx: Ctx): void {
  const doc = ctx.container;
  const state = ctx.state;
  const rt = ctx.rt;
  const toast = ctx.toast;
  const $ = (id: string): any => doc.querySelector('#' + id);
  const $$ = (sel: string): any => doc.querySelectorAll(sel);
  const getQ = (id: any) => ctx.getQ(id);
  const getS = (id: any) => ctx.getS(id);

  const logoDataUrl = (key: string) => (state.logos && (state.logos as any)[key]) || '';

  // ═══ TABS ══════════════════════════════════════════════════
  const views: any = {
    builder: $('view-builder'),
    runtime: $('view-runtime'),
    responses: $('view-responses'),
    saved: $('view-saved'),
    submitted: $('view-submitted'),
  };
  function showSubmitted() {
    Object.values(views).forEach((v: any) => v.classList.add('hide'));
    views.submitted.classList.remove('hide');
  }
  $$('.tab').forEach((b: any) => b.addEventListener('click', () => {
    Object.values(views).forEach((v: any) => v.classList.add('hide'));
    views[b.dataset.tab].classList.remove('hide');
    $$('.tab').forEach((t: any) => t.setAttribute('aria-selected', String(t.dataset.tab === b.dataset.tab)));
    if (b.dataset.tab === 'saved' && typeof ctx.renderSavedForms === 'function') ctx.renderSavedForms();
  }));
  function show(tab: string) { doc.querySelector(`.tab[data-tab="${tab}"]`).click(); }

  // ── Section-navigation helpers ──
  function fQOfSec(sid: any) { return getS(sid)?.questionIds?.[0] || null; }
  function nQInSec(sid: any, qid: any) { const s = getS(sid); if (!s) return null; const i = (s.questionIds || []).indexOf(qid); return i < 0 ? null : (s.questionIds[i + 1] || null); }
  function nSecId(sid: any) { const i = state.sections.findIndex((s: any) => s.id === sid); return i >= 0 && state.sections[i + 1] ? state.sections[i + 1].id : null; }
  function secOfQ(qid: any) { return state.sections.find((s: any) => (s.questionIds || []).includes(qid))?.id || null; }

  // ── Value readers for "Checkbox + Other" ──
  function readMultiOther(el: any) {
    const out = [...el.querySelectorAll('input[type=checkbox]:checked')].filter((cb: any) => !cb.dataset.other).map((cb: any) => cb.value);
    const ocb = el.querySelector('input[type=checkbox][data-other]');
    const oInp = el.querySelector('input[data-other-text]');
    if (ocb && ocb.checked && oInp && oInp.value.trim()) out.push(oInp.value.trim());
    return out;
  }
  function readSingleOther(el: any) {
    const orb = el.querySelector('input[type=radio][data-other]');
    const oInp = el.querySelector('input[data-other-text]');
    if (orb && orb.checked) return (oInp && oInp.value.trim()) || '';
    const checked = el.querySelector('input[type=radio]:checked');
    return checked ? checked.value : '';
  }

  function buildRuntime() {
    $('rt-title').textContent = state.title;
    $('rt-desc').textContent = state.description || '';
    const lw = $('rt-logo-wrap'); lw.innerHTML = '';
    const lurl = logoDataUrl(state.logo);
    if (lurl) { const limg = document.createElement('img'); limg.src = lurl; limg.className = 'rt-logo'; limg.alt = 'Logo'; lw.appendChild(limg); }
    const cw = $('rt-cover-wrap'); cw.innerHTML = '';
    if (state.coverMedia) { if (state.coverMedia.type === 'image') { const img = document.createElement('img'); img.src = state.coverMedia.dataUrl; img.className = 'rt-cover-img'; cw.appendChild(img); } else { const vid = document.createElement('video'); vid.src = state.coverMedia.dataUrl; vid.className = 'rt-cover-vid'; vid.controls = true; cw.appendChild(vid); } }
    // Engagement details (client/project/delivered date) — optional context
    // set once by the form creator (Settings panel), shown to every
    // respondent under the description when filled in.
    const metaEl = $('rt-meta');
    const metaItems: string[] = [];
    if (state.clientName) metaItems.push(`<span class="rt-meta-item">${esc(state.clientName)}</span>`);
    if (state.projectName) metaItems.push(`<span class="rt-meta-item">${esc(state.projectName)}</span>`);
    if (state.deliveredDate) {
      const d = new Date(state.deliveredDate + 'T00:00:00');
      const label = isNaN(d.getTime()) ? state.deliveredDate : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      metaItems.push(`<span class="rt-meta-item">Delivered ${esc(label)}</span>`);
    }
    metaEl.innerHTML = metaItems.join('<span class="rt-meta-sep">·</span>');
    metaEl.classList.toggle('hide', metaItems.length === 0);
    rt.answers = {};
    rt.mode = 'step';
    rt.fillingStartedAt = new Date().toISOString();
    ctx.secHistory.length = 0;
    $('rt-progress-wrap').style.display = '';
    if (state.sections.length) {
      rt.currentSection = state.sections[0].id; rt.currentQ = null;
      renderCurrentSection();
    } else {
      rt.currentSection = null; rt.currentQ = state.questions[0]?.id || null;
      renderCurrent();
    }
    updateProgress();
  }

  // A section only ever leads to Submit — never to another section — when
  // it's last by order, has no defaultNext override elsewhere, and none of
  // its questions carry a value-based branch rule that could redirect
  // mid-fill. Only then can we safely skip the "Next" click and go straight
  // to Submit; any conditional branching still has to be resolved live via
  // goNext() once the respondent has actually answered.
  function sectionAlwaysEndsHere(sec: any): boolean {
    if (!sec) return false;
    const sIdx = state.sections.findIndex((s: any) => s.id === sec.id);
    if (sIdx < state.sections.length - 1) return false;
    const dn = sec.defaultNext || '';
    if (dn && dn !== 'end') return false;
    const secQs = (sec.questionIds || []).map((id: any) => getQ(id)).filter(Boolean);
    return !secQs.some((q: any) => {
      const isCo = q.type === 'CHECKBOX_OTHER' && (q.meta?.mode || 'multi') === 'single';
      if (!['SINGLE_CHOICE', 'DROPDOWN'].includes(q.type) && !isCo) return false;
      const bv = q.meta?.branch?.byValue || {};
      return Object.values(bv).some((v: any) => v);
    });
  }

  function renderCurrentSection() {
    const root = $('rt-fields'); root.innerHTML = '';
    const sec = getS(rt.currentSection); if (!sec) { root.innerHTML = '<p class="muted txt-sm" style="padding:20px 0;text-align:center">No section found.</p>'; return; }
    const showSecIdentity = state.sections.length > 1;
    const badge = $('rt-sec-badge');
    badge.classList.toggle('hide', !showSecIdentity);
    if (showSecIdentity) {
      $('rt-sec-name').textContent = sec.title || '';
      const sh = document.createElement('div'); sh.className = 'rt-section-header';
      sh.innerHTML = `<div class="rt-section-title">${esc(sec.title)}</div>`;
      root.appendChild(sh);
    }
    const secQs = (sec.questionIds || []).map((id: any) => getQ(id)).filter((q: any) => q && q.type !== 'HIDDEN');
    if (!secQs.length) { root.innerHTML += '<p class="muted txt-sm" style="padding:10px 0">No questions in this section.</p>'; }
    secQs.forEach((q: any, idx: number) => {
      const wrap = document.createElement('div'); wrap.className = 'rt-field';
      const lbl = document.createElement('label'); lbl.className = 'rt-label';
      lbl.innerHTML = `<span style="margin-right:5px">${idx + 1}.</span>` + esc(q.label) + (q.required ? '<span class="req">*</span>' : '');
      wrap.appendChild(lbl);
      const input = buildRtInput(q, rt.answers[q.id]);
      if (input) { wrap.appendChild(input); attachSkipListeners(q, input); }
      root.appendChild(wrap);
    });
    const sIdx = state.sections.findIndex((s: any) => s.id === rt.currentSection);
    const isLast = sIdx >= state.sections.length - 1;
    const nb = $('rt-next'), sb = $('rt-submit');
    const prevBtn = $('rt-prev-sec');
    if (sectionAlwaysEndsHere(sec)) {
      nb.classList.add('hide'); sb.classList.remove('hide');
    } else {
      nb.textContent = isLast ? 'Next →' : 'Next Section →';
      nb.classList.remove('hide'); sb.classList.add('hide');
    }
    prevBtn.classList.toggle('hide', ctx.secHistory.length === 0);
    updateProgress();
    window.scrollTo(0, 0);
  }

  function buildRtInput(q: any, savedVal: any) {
    let input: any = null;
    const t = q.type;
    const now = new Date(), pad = (n: number) => String(n).padStart(2, '0');
    const nowDate = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    const nowTime = pad(now.getHours()) + ':' + pad(now.getMinutes());
    if (t === 'SHORT_TEXT') {
      input = document.createElement('input'); input.type = 'text'; input.dataset.qid = q.id;
      if (q.meta?.placeholder) input.placeholder = q.meta.placeholder;
      if (savedVal != null) input.value = savedVal;
    }
    else if (t === 'LONG_TEXT') { input = document.createElement('textarea'); input.rows = 4; input.dataset.qid = q.id; if (q.meta?.placeholder) input.placeholder = q.meta.placeholder; if (savedVal != null) input.value = savedVal; }
    else if (t === 'EMAIL') {
      const ec = document.createElement('div'); ec.dataset.qid = q.id; ec.dataset.type = 'EMAIL';
      const ei = document.createElement('input'); ei.type = 'text'; ei.placeholder = 'name@example.com'; if (savedVal != null) ei.value = savedVal;
      const ee = document.createElement('div'); ee.className = 'email-err'; ee.textContent = '⚠ Invalid email — must contain "@" and a domain (e.g. name@example.com).';
      ei.addEventListener('input', () => { const v = ei.value; ee.style.display = (v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) ? 'block' : 'none'; });
      ec.append(ei, ee); input = ec;
    }
    else if (t === 'PHONE') {
      const pc = document.createElement('div'); pc.dataset.qid = q.id; pc.dataset.type = 'PHONE'; pc.className = 'rt-phone';
      const row = document.createElement('div'); row.className = 'rt-phone-row';
      const sel = document.createElement('select'); sel.className = 'rt-phone-cc';
      COUNTRIES.forEach((c: any) => { const o = document.createElement('option'); o.value = c.iso; o.textContent = c.flag + ' +' + c.dial; o.title = c.name; sel.appendChild(o); });
      const ni = document.createElement('input'); ni.type = 'tel'; ni.className = 'rt-phone-num'; ni.placeholder = 'Phone number'; ni.inputMode = 'numeric';
      let iso = (q.meta?.defaultCountry) || 'IN', num = '';
      if (savedVal) { try { const sv = typeof savedVal === 'object' ? savedVal : JSON.parse(savedVal); if (sv && sv.iso) { iso = sv.iso; num = sv.national || ''; } } catch { num = String(savedVal); } }
      sel.value = iso; ni.value = num;
      const err = document.createElement('div'); err.className = 'email-err';
      const sync = () => { const cc = countryByIso(sel.value); pc.dataset.value = JSON.stringify({ iso: sel.value, dial: cc.dial, national: ni.value.replace(/\D/g, ''), e164: '+' + cc.dial + ni.value.replace(/\D/g, '') }); };
      const checkErr = () => { if (!ni.value.trim()) { err.style.display = 'none'; return; } const r = validatePhone(sel.value, ni.value); err.textContent = '⚠ ' + r.msg; err.style.display = r.ok ? 'none' : 'block'; };
      ni.addEventListener('input', () => { sync(); checkErr(); if (rt.mode === 'step') checkSV(); });
      sel.addEventListener('change', () => { sync(); checkErr(); });
      sync();
      row.append(sel, ni); pc.append(row, err); input = pc;
    }
    else if (t === 'NUMBER') { input = document.createElement('input'); input.type = 'number'; input.step = '1'; input.dataset.qid = q.id; if (savedVal != null) input.value = savedVal; }
    else if (t === 'DECIMAL') { input = document.createElement('input'); input.type = 'number'; input.step = 'any'; input.dataset.qid = q.id; if (savedVal != null) input.value = savedVal; }
    else if (t === 'DATE') {
      input = document.createElement('input'); input.type = 'date'; input.dataset.qid = q.id;
      input.value = savedVal || nowDate;
    }
    else if (t === 'TIME') {
      input = document.createElement('input'); input.type = 'time'; input.dataset.qid = q.id;
      input.value = savedVal || nowTime;
    }
    else if (t === 'DATETIME') {
      input = document.createElement('input'); input.type = 'datetime-local'; input.dataset.qid = q.id;
      input.value = savedVal || (nowDate + 'T' + nowTime);
    }
    else if (t === 'SINGLE_CHOICE') {
      const list = document.createElement('div'); list.className = 'rt-radio-list'; list.dataset.qid = q.id; list.dataset.type = 'SINGLE';
      (q.meta?.choices || []).forEach((c: any) => { const lbl2 = document.createElement('label'); const rb = document.createElement('input'); rb.type = 'radio'; rb.name = q.id + '_g'; rb.value = c.value; if (savedVal === c.value) rb.checked = true; const sp = document.createElement('span'); sp.textContent = c.label; lbl2.append(rb, sp); list.appendChild(lbl2); });
      input = list;
    }
    else if (t === 'MULTI_SELECT') {
      const list = document.createElement('div'); list.className = 'rt-check-list'; list.dataset.qid = q.id; list.dataset.type = 'MULTI';
      const prev = new Set(savedVal || []);
      (q.meta?.choices || []).forEach((c: any) => { const lbl2 = document.createElement('label'); const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = c.value; cb.checked = prev.has(c.value); lbl2.append(cb, ' ' + c.label); list.appendChild(lbl2); });
      input = list;
    }
    else if (t === 'CHECKBOX_OTHER') {
      const _mode = (q.meta?.mode || 'multi');
      const _otherLbl = ((q.meta?.otherLabel || '').trim() || 'Other') + ':';
      if (_mode === 'single') {
        const list = document.createElement('div'); list.className = 'rt-radio-list'; list.dataset.qid = q.id; list.dataset.type = 'SINGLEOTHER';
        const choiceVals = (q.meta?.choices || []).map((c: any) => c.value);
        const savedStr = (savedVal == null) ? '' : String(savedVal);
        const isOther = savedStr !== '' && !choiceVals.includes(savedStr);
        (q.meta?.choices || []).forEach((c: any) => {
          const lbl2 = document.createElement('label');
          const rb = document.createElement('input'); rb.type = 'radio'; rb.name = q.id + '_g'; rb.value = c.value;
          if (savedStr === c.value) rb.checked = true;
          const sp = document.createElement('span'); sp.textContent = c.label;
          lbl2.append(rb, sp); list.appendChild(lbl2);
        });
        const oLbl = document.createElement('label'); oLbl.className = 'rt-other-row';
        const orb = document.createElement('input'); orb.type = 'radio'; orb.name = q.id + '_g'; orb.dataset.other = '1';
        const oSpan = document.createElement('span'); oSpan.textContent = _otherLbl; oSpan.className = 'rt-other-lbl';
        const oInp = document.createElement('input'); oInp.type = 'text'; oInp.dataset.otherText = '1'; oInp.className = 'rt-other-input'; oInp.placeholder = 'Please specify…';
        if (isOther) { orb.checked = true; oInp.value = savedStr; }
        orb.addEventListener('change', () => { if (orb.checked) oInp.focus(); });
        oInp.addEventListener('input', () => { if (oInp.value.trim()) orb.checked = true; });
        oLbl.append(orb, oSpan, oInp); list.appendChild(oLbl);
        input = list;
      } else {
        const list = document.createElement('div'); list.className = 'rt-check-list'; list.dataset.qid = q.id; list.dataset.type = 'MULTIOTHER';
        const saved = Array.isArray(savedVal) ? savedVal.slice() : [];
        const choiceVals = (q.meta?.choices || []).map((c: any) => c.value);
        const otherText = saved.find((v: any) => !choiceVals.includes(v));
        const prev = new Set(saved);
        (q.meta?.choices || []).forEach((c: any) => { const lbl2 = document.createElement('label'); const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = c.value; cb.checked = prev.has(c.value); lbl2.append(cb, ' ' + c.label); list.appendChild(lbl2); });
        const oLbl = document.createElement('label'); oLbl.className = 'rt-other-row';
        const ocb = document.createElement('input'); ocb.type = 'checkbox'; ocb.dataset.other = '1';
        const oSpan = document.createElement('span'); oSpan.textContent = _otherLbl; oSpan.className = 'rt-other-lbl';
        const oInp = document.createElement('input'); oInp.type = 'text'; oInp.dataset.otherText = '1'; oInp.className = 'rt-other-input'; oInp.placeholder = 'Please specify…';
        if (otherText != null) { ocb.checked = true; oInp.value = otherText; }
        ocb.addEventListener('change', () => { if (ocb.checked) oInp.focus(); else oInp.value = ''; });
        oInp.addEventListener('input', () => { if (oInp.value.trim()) ocb.checked = true; });
        oLbl.append(ocb, oSpan, oInp); list.appendChild(oLbl);
        input = list;
      }
    }
    else if (t === 'DROPDOWN') {
      input = document.createElement('select'); input.dataset.qid = q.id;
      const blank = document.createElement('option'); blank.value = ''; blank.textContent = '— Select —'; input.appendChild(blank);
      (q.meta?.choices || []).forEach((c: any) => { const o = document.createElement('option'); o.value = c.value; o.textContent = c.label; input.appendChild(o); });
      if (savedVal) input.value = savedVal;
    }
    else if (t === 'RANKING') {
      const rk = document.createElement('div'); rk.className = 'rt-ranking'; rk.dataset.qid = q.id; rk.dataset.type = 'RANKING';
      const choices = (q.meta?.choices || []).map((c: any) => c.label);
      const selCount = q.meta?.rankSelectCount || 0;
      const order = savedVal ? String(savedVal).split(',') : [];
      const selected = savedVal ? [...order] : [];
      function renderRkItems() {
        const rankZone = rk.querySelector('.rt-rank-zone'); if (!rankZone) return;
        rankZone.innerHTML = '';
        order.forEach((item: any, i: number) => {
          const el = document.createElement('div'); el.className = 'rt-rank-item'; el.draggable = true; el.dataset.idx = String(i);
          el.innerHTML = `<span class="rt-rank-handle">⠿</span><span class="rt-rank-num">${i + 1}</span><span>${esc(item)}</span>`;
          el.addEventListener('dragstart', (e: any) => { e.dataTransfer.setData('ridx', String(i)); });
          el.addEventListener('dragover', (e: any) => { e.preventDefault(); el.classList.add('drag-target'); });
          el.addEventListener('dragleave', () => el.classList.remove('drag-target'));
          el.addEventListener('drop', (e: any) => { e.preventDefault(); el.classList.remove('drag-target'); const from = +e.dataTransfer.getData('ridx'); if (from !== i) { const [mv] = order.splice(from, 1); order.splice(i, 0, mv); rk.dataset.value = order.join(','); renderRkItems(); } });
          rankZone.appendChild(el);
        });
      }
      if (selCount > 0 && !savedVal) {
        const selWrap = document.createElement('div'); selWrap.className = 'rt-rank-select-phase';
        const hint = document.createElement('div'); hint.className = 'rt-rank-hint'; hint.textContent = 'Select ' + selCount + ' option' + (selCount > 1 ? 's' : '') + ' to rank:';
        selWrap.appendChild(hint);
        const rankZone = document.createElement('div'); rankZone.className = 'rt-rank-zone hide';
        choices.forEach((label: any) => {
          const row = document.createElement('label'); row.className = 'rt-rank-pick';
          const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = label;
          const sp = document.createElement('span'); sp.textContent = label;
          cb.addEventListener('change', () => {
            if (cb.checked) {
              if (selected.length >= selCount) { cb.checked = false; toast('You can only select ' + selCount + ' option' + (selCount > 1 ? 's' : '') + '.', 'info'); return; }
              selected.push(label);
            } else {
              const idx = selected.indexOf(label); if (idx > -1) selected.splice(idx, 1);
            }
            hint.textContent = 'Selected ' + selected.length + ' of ' + selCount;
            if (selected.length === selCount) {
              order.length = 0; order.push(...selected);
              rk.dataset.value = order.join(',');
              selWrap.classList.add('hide');
              rankZone.classList.remove('hide');
              renderRkItems();
            }
          });
          row.append(cb, sp); selWrap.appendChild(row);
        });
        const resetBtn = document.createElement('button'); resetBtn.type = 'button'; resetBtn.className = 'btn ghost sm'; resetBtn.style.marginTop = '8px'; resetBtn.textContent = '↩ Re-select options';
        resetBtn.addEventListener('click', () => {
          selected.length = 0; order.length = 0; rk.dataset.value = '';
          selWrap.querySelectorAll('input[type=checkbox]').forEach((c: any) => c.checked = false);
          hint.textContent = 'Select ' + selCount + ' option' + (selCount > 1 ? 's' : '') + ' to rank:';
          rankZone.classList.add('hide'); selWrap.classList.remove('hide');
        });
        rk.append(selWrap, rankZone, resetBtn);
      } else {
        if (!savedVal) { order.length = 0; order.push(...choices); }
        const rankZone = document.createElement('div'); rankZone.className = 'rt-rank-zone';
        rk.appendChild(rankZone);
        rk.dataset.value = order.join(','); renderRkItems();
      }
      input = rk;
    }
    else if (t === 'RATING') {
      const rrow = document.createElement('div'); rrow.className = 'rating-row'; rrow.dataset.qid = q.id; rrow.dataset.type = 'RATING';
      let selV = savedVal || null;
      for (let i = 1; i <= (q.meta?.max || 10); i++) {
        const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'rating-btn'; btn.textContent = String(i);
        if (selV == i) btn.classList.add('active');
        btn.addEventListener('click', () => { rrow.querySelectorAll('.rating-btn').forEach((b: any) => b.classList.remove('active')); btn.classList.add('active'); selV = i; rrow.dataset.value = String(i); if (rt.mode === 'step') checkSV(); });
        rrow.appendChild(btn);
      }
      if (selV) rrow.dataset.value = selV; input = rrow;
    }
    else if (t === 'LINEAR_SCALE') {
      const lsw = document.createElement('div'); lsw.dataset.qid = q.id; lsw.dataset.type = 'SCALE';
      const min = q.meta?.min ?? 1, max = q.meta?.max ?? 5, step = q.meta?.step ?? 1;
      const lrow = document.createElement('div'); lrow.className = 'ls-lbls';
      lrow.innerHTML = `<span>${esc(q.meta?.minLabel || String(min))}</span><span>${esc(q.meta?.maxLabel || String(max))}</span>`;
      const btns = document.createElement('div'); btns.className = 'ls-btns';
      let selV = savedVal || null;
      for (let v = min; v <= max; v += step) {
        const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'ls-btn'; btn.textContent = String(v);
        if (selV == v) btn.classList.add('active');
        btn.addEventListener('click', () => { btns.querySelectorAll('.ls-btn').forEach((b: any) => b.classList.remove('active')); btn.classList.add('active'); selV = v; lsw.dataset.value = String(v); if (rt.mode === 'step') checkSV(); });
        btns.appendChild(btn);
      }
      if (selV) lsw.dataset.value = selV; lsw.append(lrow, btns); input = lsw;
    }
    else if (t === 'RANGE') {
      const rw = document.createElement('div'); rw.className = 'rt-range-wrap'; rw.dataset.qid = q.id; rw.dataset.type = 'RANGE';
      const min = q.meta?.min ?? 0, max = q.meta?.max ?? 100, step = q.meta?.step ?? 1, initV = savedVal ?? min;
      const vd = document.createElement('div'); vd.className = 'rt-range-val'; vd.textContent = initV;
      const ri = document.createElement('input'); ri.type = 'range'; ri.min = min; ri.max = max; ri.step = step; ri.value = initV;
      ri.addEventListener('input', () => { vd.textContent = ri.value; rw.dataset.value = ri.value; });
      const lb = document.createElement('div'); lb.className = 'rt-range-lbls';
      lb.innerHTML = `<span>${esc(q.meta?.minLabel || String(min))}</span><span>${esc(q.meta?.maxLabel || String(max))}</span>`;
      rw.dataset.value = initV; rw.append(vd, ri, lb); input = rw;
    }
    else if (t === 'MULTI_CHOICE_GRID' || t === 'TICK_BOX_GRID' || t === 'QUESTION_MATRIX') {
      const isCheck = t === 'TICK_BOX_GRID';
      const tbl = document.createElement('table'); tbl.className = 'rt-grid-tbl'; tbl.dataset.qid = q.id; tbl.dataset.type = isCheck ? 'GCHECK' : 'GRADIO';
      const thead = document.createElement('thead'), hrow = document.createElement('tr'); hrow.innerHTML = '<th></th>';
      (q.meta?.cols || ['Option 1', 'Option 2']).forEach((c: any) => { const th = document.createElement('th'); th.textContent = c; hrow.appendChild(th); });
      thead.appendChild(hrow); tbl.appendChild(thead);
      const tbody = document.createElement('tbody');
      const prev = (savedVal && typeof savedVal === 'object') ? savedVal : {};
      (q.meta?.rows || ['Row 1', 'Row 2']).forEach((rowLbl: any) => {
        const tr = document.createElement('tr');
        const td0 = document.createElement('td'); td0.textContent = rowLbl; tr.appendChild(td0);
        (q.meta?.cols || ['Option 1', 'Option 2']).forEach((col: any) => {
          const td = document.createElement('td'); td.style.textAlign = 'center';
          const inp2 = document.createElement('input'); inp2.type = isCheck ? 'checkbox' : 'radio'; inp2.name = q.id + '_' + rowLbl; inp2.value = col;
          if (isCheck) { if ((prev[rowLbl] || []).includes(col)) inp2.checked = true; } else { if (prev[rowLbl] === col) inp2.checked = true; }
          td.appendChild(inp2); tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      tbl.appendChild(tbody); input = tbl;
    }
    else if (t === 'PHOTO' || t === 'AUDIO' || t === 'VIDEO' || t === 'FILE_UPLOAD') {
      const zone = document.createElement('div'); zone.className = 'rt-file-zone'; zone.dataset.qid = q.id; zone.dataset.type = 'FILE';
      const acceptMap: any = { PHOTO: 'image/*', AUDIO: 'audio/*', VIDEO: 'video/*', FILE_UPLOAD: '*' };
      const iconMap: any = { PHOTO: '📷', AUDIO: '🎵', VIDEO: '🎥', FILE_UPLOAD: '📎' };
      const lMap: any = { PHOTO: 'Upload a photo', AUDIO: 'Upload audio', VIDEO: 'Upload a video', FILE_UPLOAD: 'Upload a file' };
      const fi = document.createElement('input'); fi.type = 'file'; fi.accept = acceptMap[t] || '*'; fi.style.display = 'none';
      const icon = document.createElement('div'); icon.className = 'fz-icon'; icon.textContent = iconMap[t];
      const p = document.createElement('p'); p.textContent = lMap[t];
      const fname = document.createElement('div'); fname.className = 'rt-file-name'; if (savedVal) fname.textContent = '✓ ' + savedVal;
      const folderInfo = document.createElement('div'); folderInfo.className = 'rt-folder-info';
      folderInfo.innerHTML = q.meta?.folder ? `📁 Saving to: <strong>${esc(q.meta.folder)}</strong>` : '📁 Saves to your downloads folder on submit';
      const maxMB = q.meta?.maxFileSizeMB || 0;
      if (maxMB > 0) { const sizeNote = document.createElement('div'); sizeNote.className = 'rt-file-size-note'; sizeNote.textContent = 'Max size: ' + maxMB + ' MB'; zone.appendChild(sizeNote); }
      zone.append(fi, icon, p, fname, folderInfo); zone.addEventListener('click', () => fi.click());
      fi.addEventListener('change', () => {
        if (fi.files[0]) {
          if (maxMB > 0 && fi.files[0].size > maxMB * 1024 * 1024) { toast('File too large. Maximum allowed size is ' + maxMB + ' MB.', 'err'); fi.value = ''; return; }
          fname.textContent = '✓ ' + fi.files[0].name + ' (' + ((fi.files[0].size / 1024 / 1024).toFixed(1)) + ' MB)'; zone.dataset.value = fi.files[0].name; zone._fileObj = fi.files[0]; zone._folder = q.meta?.folder || '';
        }
      });
      input = zone;
    }
    else if (t === 'SIGNATURE') {
      const sw = document.createElement('div'); sw.dataset.qid = q.id; sw.dataset.type = 'SIG';
      const canvas = document.createElement('canvas'); canvas.className = 'rt-sig-canvas'; canvas.width = 640; canvas.height = 150;
      const cctx: any = canvas.getContext('2d'); cctx.strokeStyle = '#1a1a2e'; cctx.lineWidth = 2; cctx.lineCap = 'round';
      let drawing = false, lx = 0, ly = 0;
      function getXY(e: any) { const r = canvas.getBoundingClientRect(), sc = canvas.width / r.width, src = e.touches ? e.touches[0] : e; return { x: (src.clientX - r.left) * sc, y: (src.clientY - r.top) * sc }; }
      canvas.addEventListener('mousedown', (e: any) => { drawing = true; const { x, y } = getXY(e); lx = x; ly = y; });
      canvas.addEventListener('mousemove', (e: any) => { if (!drawing) return; const { x, y } = getXY(e); cctx.beginPath(); cctx.moveTo(lx, ly); cctx.lineTo(x, y); cctx.stroke(); lx = x; ly = y; });
      canvas.addEventListener('mouseup', () => { drawing = false; sw.dataset.value = canvas.toDataURL(); });
      canvas.addEventListener('touchstart', (e: any) => { e.preventDefault(); drawing = true; const { x, y } = getXY(e); lx = x; ly = y; }, { passive: false });
      canvas.addEventListener('touchmove', (e: any) => { e.preventDefault(); if (!drawing) return; const { x, y } = getXY(e); cctx.beginPath(); cctx.moveTo(lx, ly); cctx.lineTo(x, y); cctx.stroke(); lx = x; ly = y; }, { passive: false });
      canvas.addEventListener('touchend', () => { drawing = false; sw.dataset.value = canvas.toDataURL(); });
      const clearBtn = document.createElement('button'); clearBtn.type = 'button'; clearBtn.className = 'btn sm ghost'; clearBtn.style.marginTop = '6px'; clearBtn.textContent = 'Clear signature';
      clearBtn.addEventListener('click', () => { cctx.clearRect(0, 0, canvas.width, canvas.height); sw.dataset.value = ''; });
      sw.append(canvas, clearBtn); input = sw;
    }
    else if (t === 'LOCATION') {
      const card = document.createElement('div'); card.className = 'rt-location-card'; card.dataset.qid = q.id; card.dataset.type = 'LOCATION';
      const ptHead = document.createElement('p'); ptHead.style.cssText = 'font-size:13px;font-weight:600;color:var(--txt);margin-bottom:8px'; ptHead.textContent = '📍 Point Coordinates';
      const ptInfo = document.createElement('p'); ptInfo.style.cssText = 'font-size:12px;color:var(--txt2);margin-bottom:10px'; ptInfo.textContent = 'Enter latitude and longitude for a specific point:';
      const latInp = document.createElement('input'); latInp.type = 'number'; latInp.step = 'any'; latInp.placeholder = 'Latitude (e.g. 1.3521)'; latInp.style.marginBottom = '8px';
      const lngInp = document.createElement('input'); lngInp.type = 'number'; lngInp.step = 'any'; lngInp.placeholder = 'Longitude (e.g. 103.8198)';
      const sep = document.createElement('hr'); sep.style.cssText = 'margin:16px 0;border:none;border-top:1px solid var(--border)';
      const mpHead = document.createElement('p'); mpHead.style.cssText = 'font-size:13px;font-weight:600;color:var(--txt);margin-bottom:6px'; mpHead.textContent = '↗ Path / Area (optional)';
      const mpInfo = document.createElement('p'); mpInfo.style.cssText = 'font-size:12px;color:var(--txt2);margin-bottom:8px'; mpInfo.textContent = 'Enter one lat,lng per line for a GPS path or polygon area:';
      const ta = document.createElement('textarea'); ta.rows = 4; ta.placeholder = 'e.g.\n1.3521,103.8198\n1.3522,103.8199\n1.3523,103.8200'; ta.style.width = '100%';
      if (savedVal) {
        try {
          const sv = JSON.parse(savedVal);
          if (sv.lat != null) latInp.value = sv.lat;
          if (sv.lng != null) lngInp.value = sv.lng;
          if (sv.path) ta.value = sv.path;
        } catch {
          if (String(savedVal).includes('\n')) ta.value = savedVal;
          else { const [lat, lng] = String(savedVal).split(','); latInp.value = lat || ''; lngInp.value = lng || ''; }
        }
      }
      const syncLoc = () => { card.dataset.value = JSON.stringify({ lat: latInp.value || null, lng: lngInp.value || null, path: ta.value || null }); };
      latInp.addEventListener('input', syncLoc); lngInp.addEventListener('input', syncLoc); ta.addEventListener('input', syncLoc);
      syncLoc();
      card.append(ptHead, ptInfo, latInp, lngInp, sep, mpHead, mpInfo, ta);
      input = card;
    }
    else if (t === 'NOTE') {
      const box = document.createElement('div'); box.className = 'rt-note-box'; box.dataset.qid = q.id; box.dataset.type = 'NOTE';
      box.textContent = q.meta?.content || q.label; input = box;
    }
    else if (t === 'ACKNOWLEDGE') {
      const aw = document.createElement('div'); aw.className = 'rt-ack-wrap'; aw.dataset.qid = q.id; aw.dataset.type = 'ACK';
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.id = 'ack_' + q.id + '_rt'; if (savedVal === 'yes') cb.checked = true;
      cb.addEventListener('change', () => { aw.dataset.value = cb.checked ? 'yes' : ''; if (rt.mode === 'step') checkSV(); });
      const al = document.createElement('label'); al.htmlFor = 'ack_' + q.id + '_rt'; al.style.cssText = 'font-size:13.5px;line-height:1.5;cursor:pointer'; al.textContent = q.meta?.content || q.label;
      aw.dataset.value = cb.checked ? 'yes' : ''; aw.append(cb, al); input = aw;
    }
    else if (t === 'CALCULATE') {
      const rd = document.createElement('input'); rd.type = 'text'; rd.readOnly = true; rd.dataset.qid = q.id;
      rd.value = 'Computed: ' + (q.meta?.formula || '(formula not set)'); rd.style.color = 'var(--txt2)'; input = rd;
    }
    else {
      input = document.createElement('input'); input.type = 'text'; input.dataset.qid = q.id; if (savedVal != null) input.value = savedVal;
    }
    if (input && !input.dataset?.qid) input.dataset.qid = q.id;
    return input;
  }

  function renderCurrent() {
    const root = $('rt-fields'); root.innerHTML = '';
    const nb = $('rt-next'), sb = $('rt-submit');
    const q = getQ(rt.currentQ);
    sb.classList.add('hide'); nb.classList.remove('hide');
    if (!q) { nb.classList.add('hide'); sb.classList.remove('hide'); return; }
    const badge = $('rt-sec-badge');
    if (rt.currentSection) { badge.classList.remove('hide'); $('rt-sec-name').textContent = getS(rt.currentSection)?.title || ''; } else badge.classList.add('hide');
    if (q.type === 'HIDDEN') { checkSV(); return; }
    const wrap = document.createElement('div'); wrap.className = 'rt-field';
    const lbl = document.createElement('label'); lbl.className = 'rt-label';
    lbl.innerHTML = esc(q.label) + (q.required ? '<span class="req">*</span>' : '');
    wrap.appendChild(lbl);
    const input = buildRtInput(q, rt.answers[q.id]);
    if (input) { wrap.appendChild(input); attachSkipListeners(q, input); }
    root.appendChild(wrap); checkSV();
    window.scrollTo(0, 0);
    setTimeout(() => { const f = root.querySelector('input:not([type=checkbox]):not([type=radio]):not([type=range]),select,textarea'); if (f) f.focus({ preventScroll: true }); }, 40);
  }

  // ── Value reader (shared) ─────────────────────────────────
  function getFieldVal(el: any) {
    const t = el.dataset.type;
    if (t === 'MULTI') return [...el.querySelectorAll('input[type=checkbox]:checked')].map((cb: any) => cb.value);
    if (t === 'MULTIOTHER') return readMultiOther(el);
    if (t === 'SINGLEOTHER') return readSingleOther(el);
    if (t === 'SINGLE') return el.querySelector('input[type=radio]:checked')?.value || '';
    if (t === 'RATING' || t === 'SCALE' || t === 'RANGE' || t === 'RANKING') return el.dataset.value || '';
    if (t === 'FILE') return el.dataset.value || '';
    if (t === 'SIG') return el.dataset.value || '';
    if (t === 'ACK') return el.dataset.value || '';
    if (t === 'EMAIL') return el.querySelector('input')?.value || '';
    if (t === 'PHONE') return el.dataset.value || '';
    if (t === 'LOCATION') return el.dataset.value || '';
    if (t === 'GRADIO' || t === 'GCHECK') { const res: any = {}; el.querySelectorAll('tbody tr').forEach((tr: any) => { const row = tr.cells[0]?.textContent; if (t === 'GCHECK') res[row] = [...tr.querySelectorAll('input:checked')].map((i: any) => i.value); else { const c = tr.querySelector('input:checked'); if (c) res[row] = c.value; } }); return res; }
    if (t === 'NOTE' || t === 'DISPLAY') return '';
    return el.value ?? '';
  }
  function downloadAttachments() {
    $$('#rt-fields [data-type=FILE]').forEach((zone: any) => {
      if (!zone._fileObj) return;
      const prefix = zone._folder ? zone._folder.replace(/[/\\:*?"<>|]/g, '_') + '_' : '';
      const a = document.createElement('a'); a.href = URL.createObjectURL(zone._fileObj);
      a.download = prefix + zone._fileObj.name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    });
  }
  function checkSV() {
    const q = getQ(rt.currentQ); if (!q) return;
    const probe = resolveNext(q, getCurVal());
    const nb = $('rt-next'), sb = $('rt-submit');
    if (probe == null) { nb.classList.add('hide'); sb.classList.remove('hide'); } else { nb.classList.remove('hide'); sb.classList.add('hide'); }
  }
  function getCurVal() {
    const el = $('rt-fields').querySelector('[data-qid]'); if (!el) return '';
    const t = el.dataset.type;
    if (t === 'MULTI') return [...el.querySelectorAll('input[type=checkbox]:checked')].map((cb: any) => cb.value);
    if (t === 'MULTIOTHER') return readMultiOther(el);
    if (t === 'SINGLEOTHER') return readSingleOther(el);
    if (t === 'SINGLE') return el.querySelector('input[type=radio]:checked')?.value || '';
    if (t === 'RATING' || t === 'SCALE' || t === 'RANGE' || t === 'RANKING') return el.dataset.value || '';
    if (t === 'FILE') return el.dataset.value || '';
    if (t === 'SIG') return el.dataset.value || '';
    if (t === 'ACK') return el.dataset.value || '';
    if (t === 'PHONE') return el.dataset.value || '';
    if (t === 'POINT') { const inps = el.querySelectorAll('input[type=number]'); return (inps[0]?.value || '') + (inps[1]?.value ? ',' + inps[1].value : ''); }
    if (t === 'GRADIO' || t === 'GCHECK') { const res: any = {}; el.querySelectorAll('tbody tr').forEach((tr: any) => { const row = tr.cells[0]?.textContent; if (t === 'GCHECK') res[row] = [...tr.querySelectorAll('input:checked')].map((i: any) => i.value); else { const c = tr.querySelector('input:checked'); if (c) res[row] = c.value; } }); return res; }
    if (t === 'NOTE' || t === 'DISPLAY') return '';
    return el.value ?? '';
  }
  function validateCurrent() {
    const q = getQ(rt.currentQ); if (!q) return true;
    if (['HIDDEN', 'NOTE'].includes(q.type)) return true;
    const val = getCurVal();
    if (q.type === 'PHONE') { const p = parsePhoneVal(val); if (!q.required && !p.national) return true; const r = validatePhone(p.iso, p.national); if (!r.ok) { toast(r.msg, 'err'); return false; } return true; }
    if (q.required) { const empty = Array.isArray(val) ? val.length === 0 : (val === null || val === undefined || String(val).trim() === ''); if (empty) return false; }
    if (q.meta?.regex && !Array.isArray(val) && val) { try { if (!new RegExp(q.meta.regex).test(String(val))) return false; } catch { /* ignore bad regex */ } }
    return true;
  }
  function resolveNext(q: any, val: any) {
    const _coSingle = q.type === 'CHECKBOX_OTHER' && (q.meta?.mode || 'multi') === 'single';
    if ((q.type === 'SINGLE_CHOICE' || q.type === 'DROPDOWN' || _coSingle) && typeof val === 'string' && val) {
      const rule = q.meta?.branch?.byValue?.[val];
      if (rule === 'block') {
        const choice = q.meta?.choices?.find((c: any) => c.value === val || c.label === val);
        return { blocked: true, msg: choice?.blockMsg || 'You cannot proceed with this selection.' };
      }
      if (rule === 'end') return null;
      if (isST(rule)) { const sid = sidFT(rule), fq = fQOfSec(sid); return fq ? { qid: fq, section: sid } : null; }
      if (rule) return { qid: rule, section: secOfQ(rule) || rt.currentSection };
    }
    const dn = q.meta?.branch?.defaultNext;
    if (dn === 'end') return null;
    if (isST(dn)) { const sid = sidFT(dn), fq = fQOfSec(sid); return fq ? { qid: fq, section: sid } : null; }
    if (dn) return { qid: dn, section: secOfQ(dn) || rt.currentSection };
    if (rt.currentSection) {
      const nq = nQInSec(rt.currentSection, q.id); if (nq) return { qid: nq, section: rt.currentSection };
      const sec = getS(rt.currentSection), sn = sec?.defaultNext;
      if (sn === 'end') return null;
      if (isST(sn)) { const sid = sidFT(sn), fq = fQOfSec(sid); return fq ? { qid: fq, section: sid } : null; }
      if (sn) return { qid: sn, section: secOfQ(sn) || rt.currentSection };
      const nsid = nSecId(rt.currentSection); if (nsid) { const fq = fQOfSec(nsid); if (fq) return { qid: fq, section: nsid }; }
      return null;
    }
    const idx = state.questions.findIndex((qq: any) => qq.id === q.id);
    return state.questions[idx + 1] ? { qid: state.questions[idx + 1].id, section: null } : null;
  }

  // ── Immediate skip-logic on choice selection ─────────────
  function attachSkipListeners(q: any, inputEl: any) {
    if (!inputEl) return;
    const _coSingle = q.type === 'CHECKBOX_OTHER' && (q.meta?.mode || 'multi') === 'single';
    if (!['SINGLE_CHOICE', 'DROPDOWN'].includes(q.type) && !_coSingle) return;
    function execSkip(val: any) {
      if (!val) return;
      const rule = q.meta?.branch?.byValue?.[val];
      if (!rule) return;
      if (rule === 'block') {
        const choice = q.meta?.choices?.find((c: any) => c.value === val || c.label === val);
        showBlockDialog(choice?.blockMsg || 'You cannot proceed with this selection.');
        return;
      }
      if (state.sections.length > 0) return;
      $$('#rt-fields [data-qid]').forEach((el: any) => {
        if (el.dataset.qid) rt.answers[el.dataset.qid] = getFieldVal(el);
      });
      if (rule === 'end') {
        $('rt-next').classList.add('hide');
        $('rt-submit').classList.remove('hide');
        toast('You\'ve reached the end of the form. Click Submit when ready.', 'info', 4000);
        return;
      }
      if (isST(rule)) {
        const sid = sidFT(rule);
        const fq = fQOfSec(sid);
        if (fq) { rt.currentQ = fq; rt.currentSection = sid; renderCurrent(); updateProgress(); }
        return;
      }
      const targetSec = secOfQ(rule);
      rt.currentQ = rule; rt.currentSection = targetSec || rt.currentSection;
      renderCurrent(); updateProgress();
    }
    if (q.type === 'SINGLE_CHOICE') {
      inputEl.querySelectorAll('input[type=radio]').forEach((rb: any) => {
        rb.addEventListener('change', () => execSkip(rb.value));
      });
    } else if (q.type === 'DROPDOWN') {
      inputEl.addEventListener('change', () => execSkip(inputEl.value));
    } else if (_coSingle) {
      inputEl.querySelectorAll('input[type=radio]').forEach((rb: any) => {
        if (rb.dataset.other) return;
        rb.addEventListener('change', () => execSkip(rb.value));
      });
    }
  }

  function goNext() {
    // ── Section mode (sections exist in step mode) ────────
    if (rt.mode === 'step' && state.sections.length > 0) {
      const sec = getS(rt.currentSection); if (!sec) return;
      const secQs = (sec.questionIds || []).map((id: any) => getQ(id)).filter((q: any) => q && !['HIDDEN', 'NOTE'].includes(q.type));
      for (const q of secQs) {
        const el = $('rt-fields').querySelector(`[data-qid="${q.id}"]`);
        if (!el) continue;
        const val = getFieldVal(el);
        const empty = Array.isArray(val) ? val.length === 0 : (val === null || val === undefined || String(val).trim() === '');
        if (q.required && empty) { toast('Required: "' + q.label + '" must be filled in.', 'err'); return; }
        if (q.type === 'PHONE') { const p = parsePhoneVal(val); if (p.national || q.required) { const r = validatePhone(p.iso, p.national); if (!r.ok) { toast('"' + q.label + '": ' + r.msg, 'err'); return; } } }
      }
      $$('#rt-fields [data-qid]').forEach((el: any) => { if (el.dataset.qid) rt.answers[el.dataset.qid] = getFieldVal(el); });
      const _qDests: any[] = []; const _qDestSrc: any[] = []; let _qBlock: any = null;
      for (const qq of secQs) {
        const isCo = qq.type === 'CHECKBOX_OTHER' && (qq.meta?.mode || 'multi') === 'single';
        if (!['SINGLE_CHOICE', 'DROPDOWN'].includes(qq.type) && !isCo) continue;
        const v = rt.answers[qq.id];
        if (typeof v !== 'string' || !v) continue;
        const r = qq.meta?.branch?.byValue?.[v];
        if (!r) continue;
        if (r === 'block') {
          const ch = qq.meta?.choices?.find((c: any) => c.value === v || c.label === v);
          _qBlock = { msg: ch?.blockMsg || 'You cannot proceed with this selection.' };
          break;
        }
        _qDests.push(r);
        _qDestSrc.push({ qLabel: qq.label, rule: r });
      }
      if (_qBlock) { showBlockDialog(_qBlock.msg); return; }
      const _uniqueDests = [...new Set(_qDests)];
      if (_uniqueDests.length > 1) {
        const friendly = (d: any) => {
          if (d === 'end') return 'End of form';
          if (isST(d)) return getS(sidFT(d))?.title || d;
          return getQ(d)?.label ? ('Question "' + getQ(d).label + '"') : d;
        };
        const groups: any = {};
        _qDestSrc.forEach((r: any) => { (groups[r.rule] = groups[r.rule] || []).push(r.qLabel); });
        const lines = Object.entries(groups).map(([r, qs]: any) => '"' + qs.join('", "') + '" → ' + friendly(r));
        toast('Conflict — answers point to different destinations: ' + lines.join(' ; ') + '. Pick consistent answers to proceed.', 'err', 8000);
        return;
      }
      if (_uniqueDests.length === 1) {
        const dest = _uniqueDests[0];
        if (dest === 'end') {
          $('rt-next').classList.add('hide');
          $('rt-submit').classList.remove('hide');
          toast('You\'ve reached the end of the form. Click Submit when ready.', 'info', 4000);
          return;
        }
        if (isST(dest)) {
          const sid = sidFT(dest);
          if (getS(sid)) { ctx.secHistory.push(rt.currentSection); rt.currentSection = sid; renderCurrentSection(); updateProgress(); return; }
        } else {
          const targetSec = secOfQ(dest);
          if (targetSec) { ctx.secHistory.push(rt.currentSection); rt.currentSection = targetSec; renderCurrentSection(); updateProgress(); return; }
        }
      }
      const dn = sec.defaultNext || '';
      if (dn === 'end') {
        $('rt-next').classList.add('hide');
        $('rt-submit').classList.remove('hide');
        toast('All sections complete. Click Submit to finalise.', 'info', 4000);
        return;
      }
      if (dn && isST(dn)) {
        const targetSid = sidFT(dn);
        if (getS(targetSid)) { ctx.secHistory.push(rt.currentSection); rt.currentSection = targetSid; renderCurrentSection(); updateProgress(); return; }
      }
      const sIdx = state.sections.findIndex((s: any) => s.id === rt.currentSection);
      if (sIdx < state.sections.length - 1) {
        ctx.secHistory.push(rt.currentSection);
        rt.currentSection = state.sections[sIdx + 1].id;
        renderCurrentSection();
      } else {
        $('rt-next').classList.add('hide');
        $('rt-submit').classList.remove('hide');
        toast('All sections complete. Click Submit to finalise.', 'info', 4000);
      }
      return;
    }
    // ── Question-by-question mode (no sections) ──────────
    if (!validateCurrent()) { toast('Please complete this field correctly.', 'err'); return; }
    const q = getQ(rt.currentQ); rt.answers[q.id] = getCurVal();
    const next = resolveNext(q, rt.answers[q.id]);
    if (next?.blocked) { showBlockDialog(next.msg); return; }
    if (!next) { submitRuntime(); return; }
    rt.currentQ = next.qid; rt.currentSection = next.section ?? rt.currentSection; renderCurrent(); updateProgress();
  }

  function showBlockDialog(msg: string) {
    $('block-dialog-msg').textContent = msg || 'You cannot proceed with this selection.';
    $('block-dialog-overlay').classList.remove('hide');
  }

  function updateProgress() {
    const barEl = $('rt-bar'), pctEl = $('rt-pct'), lblEl = $('rt-sec-label');
    // Section-based progress only makes sense with more than one section —
    // with exactly one, "Section 1 of 1" is just noise, so fall through to
    // the plain answered-count progress below instead.
    if (rt.mode === 'step' && state.sections.length > 1) {
      const sIdx = state.sections.findIndex((s: any) => s.id === rt.currentSection);
      const pct = Math.round(((sIdx) / (state.sections.length)) * 100);
      if (barEl) barEl.style.width = pct + '%'; if (pctEl) pctEl.textContent = pct + '%';
      if (lblEl) lblEl.textContent = 'Section ' + (sIdx + 1) + ' of ' + state.sections.length + ' — ' + getS(rt.currentSection)?.title;
      return;
    }
    const total = state.questions.filter((q: any) => !['HIDDEN', 'NOTE'].includes(q.type)).length;
    const answered = Object.keys(rt.answers).length;
    const pct = total ? Math.round((answered / total) * 100) : 0;
    if (barEl) barEl.style.width = pct + '%'; if (pctEl) pctEl.textContent = pct + '%';
    const secT = (state.sections.length > 1 && rt.currentSection) ? getS(rt.currentSection)?.title : null;
    if (lblEl) lblEl.textContent = secT ? 'Section: ' + secT : answered + ' of ' + total + ' answered';
  }

  function captureGeoLocation(): Promise<any> {
    return new Promise(resolve => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy), capturedAt: new Date().toISOString() }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }

  // Validates + collects every field in the currently-rendered section into
  // rt.answers. goNext() already does this when advancing between sections —
  // but a section that sectionAlwaysEndsHere() resolves straight to Submit
  // (see renderCurrentSection()) never calls goNext(), so submitRuntime()
  // has to do this collection itself or the section's answers are lost.
  function collectAndValidateCurrentSection(): boolean {
    const sec = getS(rt.currentSection);
    if (!sec) return true;
    const secQs = (sec.questionIds || []).map((id: any) => getQ(id)).filter((q: any) => q && !['HIDDEN', 'NOTE'].includes(q.type));
    for (const q of secQs) {
      const el = $('rt-fields').querySelector(`[data-qid="${q.id}"]`);
      if (!el) continue;
      const val = getFieldVal(el);
      const empty = Array.isArray(val) ? val.length === 0 : (val === null || val === undefined || String(val).trim() === '');
      if (q.required && empty) { toast('Required: "' + q.label + '" must be filled in.', 'err'); return false; }
      if (q.type === 'PHONE') { const p = parsePhoneVal(val); if (p.national || q.required) { const r = validatePhone(p.iso, p.national); if (!r.ok) { toast('"' + q.label + '": ' + r.msg, 'err'); return false; } } }
    }
    $$('#rt-fields [data-qid]').forEach((el: any) => { if (el.dataset.qid) rt.answers[el.dataset.qid] = getFieldVal(el); });
    return true;
  }

  async function submitRuntime() {
    if (rt.currentSection) {
      if (!collectAndValidateCurrentSection()) return;
    } else {
      const curQ = getQ(rt.currentQ);
      if (curQ && !['HIDDEN', 'NOTE'].includes(curQ.type)) { if (!validateCurrent()) { toast('Please complete this field correctly.', 'err'); return; } rt.answers[curQ.id] = getCurVal(); }
    }
    downloadAttachments();
    let respondentLocation = null;
    if (state.captureLocation) {
      toast('Capturing location…', 'info', 2000);
      respondentLocation = await captureGeoLocation();
      if (!respondentLocation) toast('Location unavailable — response saved without coordinates.', 'info', 3000);
    }
    const response = { id: 'r' + Date.now(), createdAt: new Date().toISOString(), fillingStartedAt: rt.fillingStartedAt || null, fillingEndedAt: new Date().toISOString(), answers: Object.entries(rt.answers).map(([questionId, value]) => ({ questionId, value })), respondentLocation };
    // Public respondents post through the unauthenticated endpoint and never
    // see the Responses tab (that would leak every other respondent's
    // answers) — they land on a plain thank-you screen instead.
    if (ctx.public) {
      try {
        await postPublicResponse(ctx.formId, response);
        toast('Response submitted!', 'ok');
      } catch {
        toast('Could not submit — please try again.', 'err', 5000);
        return;
      }
      showSubmitted();
      return;
    }
    state.responses.push(response);
    // Persist to server (fire-and-forget). Legacy used fetch(...).then(if r.ok
    // setServerStatus(true)).catch(setServerStatus(false)); axios rejects on
    // non-2xx, so any failure path lands in .catch → offline status.
    postResponse(ctx.formId, response).then(() => ctx.setServerStatus(true)).catch(() => ctx.setServerStatus(false));
    $('resp-dot').classList.add('show');
    ctx.buildResponses(); show('responses'); toast('Response submitted!', 'ok');
  }

  // ── Button wiring ──
  $('preview-btn').onclick = () => { buildRuntime(); show('runtime'); };
  $('rt-back').onclick = () => show('builder');
  $('rt-next').onclick = () => goNext();
  $('rt-submit').onclick = () => submitRuntime();
  $('rt-prev-sec').addEventListener('click', () => {
    if (rt.mode !== 'step' || !state.sections.length || !ctx.secHistory.length) return;
    $$('#rt-fields [data-qid]').forEach((el: any) => {
      if (el.dataset.qid) rt.answers[el.dataset.qid] = getFieldVal(el);
    });
    rt.currentSection = ctx.secHistory.pop();
    renderCurrentSection();
  });
  $('block-dialog-ok').addEventListener('click', () => {
    $('block-dialog-overlay').classList.add('hide');
  });
  $('block-dialog-overlay').addEventListener('click', (e: any) => {
    if (e.target === $('block-dialog-overlay')) $('block-dialog-overlay').classList.add('hide');
  });

  // ── Wire into Ctx ──
  ctx.show = show;
  ctx.buildRuntime = buildRuntime;
  ctx.renderCurrentSection = renderCurrentSection;
  ctx.renderCurrent = renderCurrent;
  ctx.goNext = goNext;
  ctx.submitRuntime = submitRuntime;
  ctx.buildRtInput = buildRtInput;
}
